import { LitElement, html } from 'lit';

/**
 * Base Icon Component
 * Abstract base class for all icon components
 * Provides consistent sizing, accessibility, and styling API
 */
class BaseIcon extends LitElement {
    static properties = {
        size: { type: Number },
        ariaLabel: { type: String, attribute: 'aria-label' },
    };

    // Use Light DOM for Tailwind compatibility
    createRenderRoot() {
        return this;
    }

    constructor() {
        super();
        this.size = 6; // Default: h-6 w-6 (24px)
        this.ariaLabel = null;
    }

    /** Tailwind-safe size map — full class strings so the scanner detects them */
    static SIZE_MAP = {
        4: 'h-4 w-4',
        5: 'h-5 w-5',
        6: 'h-6 w-6',
        8: 'h-8 w-8',
    };

    get sizeClasses() {
        return BaseIcon.SIZE_MAP[this.size] || `h-${this.size} w-${this.size}`;
    }

    /**
     * Abstract method - must be implemented by subclasses
     * Returns the SVG path/shape elements
     */
    renderIconPath() {
        throw new Error('renderIconPath() must be implemented by subclass');
    }

    /**
     * Get common SVG attributes
     * Subclasses can override to customize (fill vs stroke, viewBox, etc.)
     */
    getSvgAttributes() {
        return {
            xmlns: 'http://www.w3.org/2000/svg',
            viewBox: '0 0 24 24',
            fill: 'none',
            stroke: 'currentColor',
            'stroke-width': '2',
        };
    }

    render() {
        const attrs = this.getSvgAttributes();

        return html`
            <svg
                xmlns="${attrs.xmlns}"
                viewBox="${attrs.viewBox}"
                fill="${attrs.fill || 'none'}"
                stroke="${attrs.stroke || 'none'}"
                stroke-width="${attrs['stroke-width'] || ''}"
                class="block ${this.sizeClasses}"
                aria-hidden="${this.ariaLabel ? 'false' : 'true'}"
                aria-label="${this.ariaLabel || ''}"
                role="${this.ariaLabel ? 'img' : ''}"
            >
                ${this.renderIconPath()}
            </svg>
        `;
    }

    /**
     * Render SVG content using svg template tag
     * Subclasses should override renderIconPath() and use svg`` instead of html``
     */
    renderIconPathSvg() {
        return this.renderIconPath();
    }
}

export { BaseIcon };
